/**
 * Model settings — the platform-wide model dials (System tab).
 *
 * Each row shows the model a dial names now and where that value comes from
 * (saved here = "global", the deploy files = "env", the code = "default").
 * Owners and admins change a dial with a reason; the change is audited and
 * applies within about a minute. "Deploy default" removes the saved value.
 */
import { useState, useEffect } from 'react';
import { api } from '../../api';

const SOURCE_LABEL = {
  global: 'saved here',
  env: 'deploy settings',
  default: 'code default',
};

const PLAIN_NAME = {
  AGENT_DEFAULT_MODEL: 'Ask Arc (chat)',
};

export default function ModelSettings({ opsRole }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // { name, value, reason }
  const [saving, setSaving] = useState(false);
  const canEdit = opsRole === 'owner' || opsRole === 'admin';

  async function load() {
    setError(null);
    try {
      setData(await api.get('/ops_admin/system/models'));
    } catch (e) {
      setError(e?.message || 'Could not load the model settings');
    }
  }

  useEffect(() => { load(); }, []);

  async function save() {
    if (!editing?.reason?.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await api.post('/ops_admin/system/models', {
        name: editing.name,
        value: editing.value || null,
        reason: editing.reason.trim(),
      });
      setEditing(null);
      await load();
    } catch (e) {
      setError(e?.message || 'The change was not saved');
    } finally {
      setSaving(false);
    }
  }

  if (!data && !error) return <p className="loading-text">Loading model settings...</p>;

  return (
    <div className="ops-section">
      <h2>Model settings</h2>
      <p className="muted-text">
        Which model each part of the app uses. A change applies within about a minute.
        Available models: {(data?.choices || []).join(', ') || '—'}.
      </p>
      {error && <p className="error-text">{error}</p>}
      {data && (
        <table className="ops-table">
          <thead>
            <tr><th>Setting</th><th>Model now</th><th>From</th>{canEdit && <th />}</tr>
          </thead>
          <tbody>
            {data.settings.map((s) => {
              const isEditing = editing?.name === s.name;
              const notOffered = s.value && !(data.choices || []).includes(s.value);
              return (
                <tr key={s.name}>
                  <td>
                    <strong>{PLAIN_NAME[s.name] || s.name}</strong>
                    <div className="muted-text">{s.description}</div>
                  </td>
                  <td>
                    {isEditing ? (
                      <select
                        value={editing.value}
                        onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                      >
                        <option value="">Deploy default</option>
                        {(data.choices || []).map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    ) : (
                      <span>
                        {s.value || <em>deploy default</em>}
                        {notOffered && <span className="badge badge-suspended ml-2">not available here</span>}
                      </span>
                    )}
                  </td>
                  <td>{SOURCE_LABEL[s.source] || s.source}</td>
                  {canEdit && (
                    <td>
                      {isEditing ? (
                        <div className="ops-inline-form">
                          <input
                            type="text"
                            placeholder="Reason (required)"
                            value={editing.reason}
                            onChange={(e) => setEditing({ ...editing, reason: e.target.value })}
                          />
                          <button className="btn btn-sm btn-primary" disabled={saving || !editing.reason.trim()} onClick={save}>
                            {saving ? 'Saving…' : 'Save'}
                          </button>
                          <button className="btn btn-sm btn-ghost" disabled={saving} onClick={() => setEditing(null)}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() => setEditing({
                            name: s.name,
                            value: notOffered ? '' : (s.source === 'global' ? s.value : ''),
                            reason: '',
                          })}
                        >
                          Change
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
